import React, { useState } from 'react';

import {
  CardRelacionamento,
  RadarRelacionamentoResponse,
  RecorteDoRelacionamento,
} from '../../../../api/contac/contacRecomendacoesApi';
import { AchadoDoAssistente } from '../../../../api/radarApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import '../../../../components/como-funciona/help-circle-btn.css';
import EsqueletoDeCard from '../../../../components/radar/EsqueletoDeCard';
import SecaoAchadosDoAssistente from '../../../../components/radar/SecaoAchadosDoAssistente';
import { secoesDaProveniencia } from '../../../../components/radar/secoesDaProveniencia';
import { EstadoDoRadarRelacionamento } from '../hooks/useRadarRelacionamento';
import DetalheRelacionamentoModal from './DetalheRelacionamentoModal';
// O visual do Radar virou CSS de COMPONENTE na at-82 — antes este painel o importava da tela
// vizinha (`views/contac/radar/pages/RadarFinanceiro.css`), que é acoplamento entre views.
import '../../../../components/radar/radar.css';
import '../../../../shared/ui/dashboard-base.css';
import '../../../../shared/feedback/erro-de-tela.css';

interface Props {
  estado: EstadoDoRadarRelacionamento;
  onBuscarPlaca: (placa: string) => void;
  /** O período usado na carga — o detalhe precisa repetir o MESMO, ou lista e cartão divergem. */
  periodoCarregado?: string;
  /**
   * O usuário já aplicou um período em Filtros?
   *
   * 🔴 Existe porque o Radar **não tem o que consultar** antes disso (o filtro define o início da
   * janela, ADR 0008) — e "não consultei ainda" e "não há inadimplência" não podem ter a mesma
   * silhueta na tela, que aqui é a mesma exigência da at-60 aplicada ao estado inicial. Sem o
   * campo, a aba abria **em branco** e a equipe de cobrança lia isso como painel quebrado.
   */
  periodoAplicado?: boolean;
}

/**
 * Radar do Relacionamento (contac/at-13, ADR 0007 do Contac) — o painel no topo do Dashboard de
 * Placas, só para quem tem o papel RELACIONAMENTO.
 *
 * ## A regra que governa este painel
 *
 * "Nenhum associado inadimplente" é um desfecho possível, e por isso falha de carga **nunca** pode
 * ter a mesma silhueta: o painel diz que não conseguiu verificar, e nomeia os cartões que
 * falharam. É a regra da at-60 com uma consequência específica — a equipe de cobrança iria embora
 * achando que não há fila.
 *
 * ## Cartão com zero continua aparecendo
 *
 * Diferente do Radar da diretoria (onde card sem achado some), aqui "0 novos inadimplentes hoje" é
 * informação de trabalho: sem o cartão, não dá para saber se olhou ou se não havia o que olhar.
 *
 * ## O filtro define o INÍCIO; o fim é sempre ontem (ADR 0008)
 *
 * 🔴 A assimetria é declarada em texto, e não é detalhe: o filtro da tela para na borda consultável
 * do Contac (dois meses atrás, pela defasagem das views), enquanto a fonte deste painel é a tabela
 * de boletos, sincronizada todo dia. Respeitar o fim do filtro zeraria o cartão de quem acabou de
 * deixar de pagar — 1.266 associados sumiriam, medido em 05/09/2026.
 */
const RadarRelacionamentoPanel: React.FC<Props> = ({
  estado,
  onBuscarPlaca,
  periodoCarregado,
  periodoAplicado = false,
}) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [cardExplicado, setCardExplicado] = useState<CardRelacionamento | null>(null);
  const [detalheAberto, setDetalheAberto] = useState<CardRelacionamento | null>(null);
  /** O achado cuja lista de associados está aberta (06/09/2026). */
  const [recorteAberto, setRecorteAberto] = useState<AchadoDoAssistente | null>(null);

  const { dados, carregando, erro } = estado;

  return (
    <section className="radar-relacionamento">
      {/*
        Sem título próprio: a ABA já nomeia o painel (decisão do Victor, 05/09/2026). Repetir
        "Radar do Relacionamento" logo abaixo da aba selecionada seria ruído. O `?` fica junto da
        nota de janela, que é o texto que ele complementa.
      */}
      <div className="radar-relacionamento-topo">
        {dados ? (
          <p className="do-nota-janela radar-relacionamento-nota">
            Boletos que venceram <strong>a partir de {dados.periodoInicio}</strong> e seguem em
            aberto até <strong>{dados.dataReferencia}</strong>. O período do filtro define o
            começo; o fim é <strong>sempre ontem</strong>, porque os boletos são sincronizados
            diariamente — enquanto os valores da lista abaixo fecham uma vez por mês.
          </p>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="help-circle-btn"
          title="Como Funciona"
          onClick={() => setIsHelpOpen(true)}
        >
          ?
        </button>
      </div>

      {/*
        Estado inicial DECLARADO (06/09/2026): antes disto a aba abria em branco quando ninguém
        tinha aplicado filtro ainda — indistinguível de painel quebrado, e a leitura mais provável
        para quem trabalha a fila é "não há ninguém para cobrar".

        Não é a faixa de erro: nada falhou. É instrução, com o motivo — o período do filtro é o que
        diz a partir de quando olhar os boletos (ADR 0008).
      */}
      {!periodoAplicado && !carregando && !dados && !erro && (
        <p className="do-nota-janela radar-relacionamento-nota">
          Escolha o período em <strong>Filtros</strong> e clique em{' '}
          <strong>Aplicar Filtros</strong> para carregar o Radar. O período define{' '}
          <strong>a partir de quando</strong> os boletos vencidos são considerados — sem ele não há
          o que consultar. Nada foi verificado ainda: isto não é ausência de inadimplência.
        </p>
      )}

      {/*
        🔴 "Radar atualizado há X" (06/09/2026 — pergunta do Victor: "tenho certeza que os 1.225
        foram sincronizados hoje?").

        Fica NA FRENTE dos cartões, não no `?`: é o que decide se dá para ligar agora. Antes desta
        entrega a tela dizia "boletos atualizados em <data>" lendo o máximo da tabela inteira, o que
        é otimista por construção — com 1,8 milhão de linhas sempre há alguma recente.
      */}
      {/*
        🔴 at-83 — Radar DESATIVADO: bloco próprio, e ele SUBSTITUI os cartões.
        Uma fila de cobrança vazia é lida como "não há ninguém para cobrar" — a leitura mais cara
        possível nesta tela. Com o Radar desligado nada foi verificado, e é isso que precisa estar
        escrito. O carimbo de conferência também não aparece: ele afirmaria um frescor que não
        existe.
      */}
      {!carregando && dados?.desativacao && (
        <p className="radar-candidatos-indisponivel">
          O Radar está desativado pela Administração do Sistema.
          {dados.desativacao.por
            ? ` Desativado por ${dados.desativacao.por}${
                dados.desativacao.em
                  ? ` em ${new Date(dados.desativacao.em).toLocaleDateString('pt-BR')}`
                  : ''
              }.`
            : ''}{' '}
          Nenhuma verificação de inadimplência foi executada — este painel não está dizendo que não
          há quem cobrar.
        </p>
      )}

      {!carregando && dados && !dados.desativacao && <CarimboDaConferencia dados={dados} />}

      {erro && (
        <p className="erro-de-tela">
          Não foi possível carregar o Radar. Este painel não está dizendo que não há inadimplência
          — ele não conseguiu verificar. A lista de placas abaixo continua válida.
        </p>
      )}

      {dados && dados.cardsComFalha.length > 0 && (
        <p className="erro-de-tela">
          Estes cartões não puderam ser calculados agora:{' '}
          {dados.cardsComFalha.map((id) => rotuloDoCard(id)).join(', ')}. Os demais continuam
          válidos.
        </p>
      )}

      {carregando && (
        <div className="radar-cards" role="status" aria-label="Consultando a situação dos boletos">
          <EsqueletoDeCard />
          <EsqueletoDeCard />
          <EsqueletoDeCard />
          <EsqueletoDeCard />
        </div>
      )}

      {!carregando && dados && !dados.desativacao && (
        <>
          <p className="radar-recomendacao radar-relacionamento-resumo">{dados.resumo}</p>

          <div className="radar-cards">
            {dados.cards.map((card) => (
              <article className="do-card radar-card" key={card.cardId}>
                <div className="radar-card-topo">
                  <span className="do-card-titulo">{card.titulo}</span>
                  <button
                    type="button"
                    className="help-circle-btn"
                    title="De onde vem este dado"
                    onClick={() => setCardExplicado(card)}
                  >
                    ?
                  </button>
                </div>

                <div className="radar-numero">
                  <span className="radar-numero-valor">
                    {new Intl.NumberFormat('pt-BR').format(card.numero)}
                  </span>
                  <span className="radar-numero-unidade">{card.unidade}</span>
                </div>

                <div className="radar-card-acoes">
                  <button
                    type="button"
                    className="do-btn-filtros"
                    onClick={() => setDetalheAberto(card)}
                    disabled={card.numero === 0}
                    title={
                      card.numero === 0
                        ? 'Nenhum associado neste cartão no momento'
                        : 'Ver os associados deste cartão'
                    }
                  >
                    Ver associados
                  </button>
                </div>
              </article>
            ))}
          </div>

          <p className="radar-candidatos-indisponivel">
            Fora da fila de trabalho:{' '}
            {new Intl.NumberFormat('pt-BR').format(dados.contexto.atrasoLongo)} associados entre 91
            e 365 dias de atraso e{' '}
            {new Intl.NumberFormat('pt-BR').format(dados.contexto.legado)} com mais de um ano.
            {dados.origemRedacao === 'template' &&
              ' O texto acima é automático: o assistente de inteligência artificial não está configurado neste ambiente.'}
          </p>

          {/*
            Os achados do assistente (ADR 0008 do Contac, generalizados na at-82): o MESMO
            componente que desenha a seção do Radar Financeiro.

            ⚠️ O cartão do achado é idêntico aos cartões acima — a separação é feita pelo SELO e
            pelo texto de apoio, nunca pela forma (decisão do Victor, 02/09/2026: "tudo tem que ser
            padronizado"). Não reintroduzir borda tracejada nem cor própria aqui.

            🔴 A janela deles é FIXA e NÃO segue o filtro da tela (análise §3.3 da at-82): sem um
            conjunto por dia igual para todos, duas pessoas com filtros diferentes votariam em
            achados diferentes e o voto não significaria nada. Os CARTÕES acima continuam seguindo
            o filtro — a seção diz isso por extenso.
          */}
          <SecaoAchadosDoAssistente
            radarId="relacionamento"
            nivelDoTitulo="h3"
            apoio={
              <>
                Onde a inadimplência se concentra na carteira. São hipóteses levantadas por
                inteligência artificial a partir de consultas do próprio sistema — confira antes de
                agir. Os cartões acima continuam sendo a fila de trabalho.
              </>
            }
            notaDaJanela={
              <>
                <strong>Esta seção não segue o período que você aplicou nos filtros</strong> — ele
                vale para os cartões de cobrança acima.
              </>
            }
            renderAcoes={(achado) =>
              achado.recorte ? (
                <button
                  type="button"
                  className="do-btn-filtros"
                  onClick={() => setRecorteAberto(achado)}
                >
                  Ver associados
                </button>
              ) : null
            }
          />
        </>
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Radar do Relacionamento"
        secoes={secoesDeAjuda(dados?.parametros)}
      />

      {cardExplicado && (
        <ComoFunciona
          isOpen
          onClose={() => setCardExplicado(null)}
          titulo={`De onde vem: ${cardExplicado.titulo}`}
          secoes={secoesDaProveniencia(cardExplicado.proveniencia)}
        />
      )}

      {detalheAberto && (
        <DetalheRelacionamentoModal
          cardId={detalheAberto.cardId}
          titulo={detalheAberto.titulo}
          periodoInicio={periodoCarregado}
          onClose={() => setDetalheAberto(null)}
          onBuscarPlaca={onBuscarPlaca}
        />
      )}

      {/*
        A lista de um ACHADO, no MESMO modal dos cartões. O título repete o do achado para quem
        abriu não perder de vista de qual achado a lista fala.

        🔴 **Sem `periodoInicio`, de propósito** (at-82): desde a janela fixa, o achado do
        assistente é analisado sobre o período padrão do radar, e não sobre o filtro da tela.
        Mandar o `periodoCarregado` aqui abriria uma lista de tamanho diferente do número que está
        no cartão — que é exatamente o defeito que a passagem do período existia para evitar
        enquanto as duas coisas compartilhavam a janela. Os CARTÕES acima continuam mandando.
      */}
      {recorteAberto?.recorte && (
        <DetalheRelacionamentoModal
          recorte={recorteAberto.recorte as RecorteDoRelacionamento}
          titulo={recorteAberto.titulo}
          onClose={() => setRecorteAberto(null)}
          onBuscarPlaca={onBuscarPlaca}
        />
      )}
    </section>
  );
};

/**
 * "Radar atualizado há X" — o carimbo que o Victor pediu em 06/09/2026.
 *
 * 🔴 Ele responde a pergunta que originou tudo isto: *"eu tenho certeza que os 1.225 foram
 * sincronizados hoje?"*. A resposta honesta é uma DURAÇÃO, não uma data: "há 40 minutos" se lê
 * sozinho, enquanto "conferido em 06/09 14:20" obriga quem olha a fazer a conta — e é justamente
 * quem está com o telefone na mão.
 *
 * ⚠️ A data que chega é o PIOR caso da fila (a verificação mais antiga ainda válida), não a mais
 * recente: com o máximo, um associado conferido agora carimbaria a fila inteira como fresca.
 *
 * ⚠️ Os três estados são diferentes de propósito (regra da at-60): conferido (com a idade), **não
 * configurado** neste ambiente, e **ainda não conferido** — este último é o estado real logo após
 * o deploy, antes da primeira varredura, e dizer "atualizado agora" ali seria mentira.
 */
const CarimboDaConferencia: React.FC<{ dados: RadarRelacionamentoResponse }> = ({ dados }) => {
  const conferencia = dados.conferencia;

  if (!conferencia || !conferencia.disponivel) {
    return (
      <p className="do-nota-janela radar-relacionamento-nota">
        A conferência em tempo real dos boletos não está configurada neste ambiente. Os números
        abaixo vêm da última sincronização
        {dados.fonteAtualizadaEm
          ? ` do espelho (${new Date(dados.fonteAtualizadaEm).toLocaleString('pt-BR')})`
          : ''}
        , e podem não refletir pagamentos recentes.
      </p>
    );
  }

  if (!conferencia.conferidoEm) {
    return (
      <p className="do-nota-janela radar-relacionamento-nota">
        Os boletos ainda <strong>não foram conferidos</strong> na Hinova nesta janela. Os números
        abaixo vêm do espelho e podem incluir quem já pagou — ao abrir{' '}
        <strong>Ver associados</strong>, a lista é conferida na hora.
      </p>
    );
  }

  return (
    <p className="do-nota-janela radar-relacionamento-nota">
      Radar atualizado <strong>{tempoDesde(conferencia.conferidoEm)}</strong>: os boletos de{' '}
      {conferencia.associados.toLocaleString('pt-BR')}{' '}
      {conferencia.associados === 1 ? 'associado' : 'associados'} foram conferidos direto na Hinova,
      e quem já pagou saiu dos números acima. A conferência se repete a cada{' '}
      {conferencia.intervaloDeHoras}{' '}
      {conferencia.intervaloDeHoras === 1 ? 'hora' : 'horas'} no horário comercial.
    </p>
  );
};

/**
 * A idade em português, arredondada para baixo.
 *
 * Arredondar para baixo é deliberado: dizer "há 1 hora" quando faz 1h50 subestima a idade do dado,
 * e nesta tela o erro precisa cair para o lado de quem desconfia — não para o de quem confia.
 */
function tempoDesde(iso: string): string {
  const minutos = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutos < 1) return 'agora há pouco';
  if (minutos < 60) return `há ${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) {
    const resto = minutos % 60;
    return resto === 0
      ? `há ${horas} ${horas === 1 ? 'hora' : 'horas'}`
      : `há ${horas}h${String(resto).padStart(2, '0')}`;
  }

  const dias = Math.floor(horas / 24);
  return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

function secoesDeAjuda(parametros?: Record<string, number>): ComoFuncionaSecao[] {
  const recente = parametros?.diasInadimplenciaRecente ?? 30;
  const recuperacao = parametros?.diasJanelaDeRecuperacao ?? 90;
  const legado = parametros?.diasLegado ?? 365;

  return [
    {
      titulo: 'O que este painel mostra',
      conteudo: (
        <>
          <p>
            Quem está com <strong>boleto em aberto e vencido</strong>, separado por há quanto tempo
            o atraso começou. É a fila de trabalho da equipe: por onde começar hoje.
          </p>
          <p>
            Os números são <strong>quantidades de associados</strong>. Valores em dinheiro não
            aparecem aqui, como no resto da sua tela.
          </p>
        </>
      ),
    },
    {
      titulo: 'Por que boleto cancelado não conta',
      conteudo: (
        <>
          <p>
            Quando um boleto é substituído, o antigo fica registrado como <strong>cancelado</strong>{' '}
            e um novo é emitido no lugar. O cancelado nunca recebe pagamento — mas isso não
            significa que o associado deve.
          </p>
          <p>
            Por isso o painel só conta boleto <strong>em aberto</strong>. Em medição de setembro de
            2026, 93 de cada 100 boletos "vencidos e sem pagamento" estavam apenas cancelados: sem
            essa regra, a lista mandaria ligar para milhares de pessoas em dia.
          </p>
        </>
      ),
    },
    {
      titulo: 'O que cada cartão significa',
      conteudo: (
        <ul>
          <li>
            <strong>Até {recente} dias</strong>: acabou de deixar de pagar. Costuma ser esquecimento,
            troca de cartão ou boleto que não chegou — é onde o contato mais resolve.
          </li>
          <li>
            <strong>Entre {recente + 1} e {recuperacao} dias</strong>: ainda recuperável, mas costuma
            exigir negociação.
          </li>
          <li>
            <strong>Dois ou mais boletos em aberto</strong>: repetição. Lembrete raramente resolve;
            o assunto costuma ser data de vencimento, forma de pagamento ou revisão de plano.
          </li>
          <li>
            <strong>Regularizaram</strong>: quem voltou a pagar nos últimos 30 dias. É o retorno do
            trabalho aparecendo.
          </li>
        </ul>
      ),
    },
    {
      titulo: 'Como o filtro da tela afeta o painel',
      conteudo: (
        <>
          <p>
            O período que você escolhe define <strong>a partir de quando</strong> o painel olha os
            vencimentos. Filtrar três meses em vez de doze reduz a fila de verdade.
          </p>
          <p>
            <strong>Os números já vêm conferidos na Hinova.</strong> Uma verificação automática
            percorre a fila inteira a cada duas horas, no horário comercial, e pergunta boleto a
            boleto quem ainda deve — quem pagou sai dos cartões, não só da lista. A linha no topo do
            painel diz <strong>há quanto tempo</strong> isso foi feito; ao abrir{' '}
            <strong>Ver associados</strong>, quem tiver ficado para trás é conferido na hora.
          </p>
          <p>
            O painel cobre a base do <strong>Rio de Janeiro</strong>. É ela que o sistema consegue
            verificar na Hinova em tempo real, e mostrar uma fila que não dá para conferir seria
            pedir que você ligasse sem saber se a pessoa ainda deve.
          </p>
          <p>
            O <strong>fim</strong> é diferente: ele é sempre <strong>ontem</strong>, mesmo que o
            filtro termine antes. O motivo é a origem do dado — a lista de placas usa o consolidado
            financeiro, que fecha com cerca de dois meses de atraso, enquanto o painel usa os
            boletos, atualizados todos os dias.
          </p>
          <p>
            Se o painel parasse onde a lista para, ninguém com menos de dois meses de atraso
            apareceria — ou seja, justamente quem acabou de deixar de pagar, que é onde o contato
            mais funciona.
          </p>
        </>
      ),
    },
    {
      titulo: 'A conferência antes de você ligar',
      conteudo: (
        <>
          <p>
            Ao abrir <strong>Ver associados</strong>, o sistema confere os boletos daquelas pessoas{' '}
            <strong>direto na Hinova</strong>. Quem pagou depois da última sincronização sai da
            lista antes de você ligar.
          </p>
          <p>
            A conferência é compartilhada: quando um colega abre a mesma lista, ela já vem pronta.
            Cada linha mostra o horário em que foi conferida, e o rodapé diz quantas não deu tempo
            de conferir — nunca fingimos que a lista inteira foi confirmada.
          </p>
        </>
      ),
    },
    {
      titulo: 'Os achados do assistente',
      conteudo: (
        <>
          <p>
            Abaixo dos cartões, a inteligência artificial procura <strong>padrões</strong> na
            carteira: em que regional, categoria de veículo ou tipo de boleto a inadimplência se
            concentra. Os cartões dizem <em>quem procurar</em>; os achados sugerem <em>onde olhar</em>.
          </p>
          <p>
            São <strong>candidatos</strong>, não conclusões — e o número de cada um é lido pelo
            sistema da própria consulta, nunca escrito pela inteligência artificial. Proposta que
            não bate com a consulta é descartada, e o painel diz quantas foram.
          </p>
          <p>
            <strong>Ver associados</strong> abre exatamente quem compõe aquele número: a mesma fila
            dos cartões, recortada pelo achado, e conferida na Hinova do mesmo jeito. O botão só
            aparece quando o achado descreve uma fila — um número sobre a carteira inteira, ou sobre
            quem está em dia, não tem lista de cobrança a mostrar.
          </p>
          <p>
            <strong>Os achados do assistente não seguem o período que você aplicou nos
            filtros.</strong> Eles olham sempre a mesma janela, que a própria seção declara; o
            filtro vale para os <strong>cartões de cobrança</strong> acima. É assim porque os
            achados são <strong>um conjunto por dia, igual para toda a equipe</strong> — sem isso,
            duas pessoas com filtros diferentes veriam achados diferentes e a avaliação de cada uma
            não teria como ser comparada.
          </p>
        </>
      ),
    },
    {
      titulo: 'Sua avaliação dos achados',
      conteudo: (
        <>
          <p>
            Cada achado do assistente tem <strong>Útil</strong> e <strong>Não útil</strong>. O voto
            muda o que o Radar procura amanhã: um recorte que a equipe reprova{' '}
            <strong>deixa de ser proposto</strong>, e um recorte aprovado{' '}
            <strong>passa a ser acompanhado todo dia</strong>, com o número atualizado sem nova
            busca.
          </p>
          <p>
            Depois de votar você pode escolher um motivo e escrever um comentário — os dois são
            opcionais. O comentário é levado ao assistente na análise seguinte{' '}
            <strong>como opinião sobre a utilidade do achado</strong>, nunca como ordem, e fica
            gravado com o seu nome.
          </p>
          <p>
            <strong>A contagem de votos não aparece aqui</strong>, de propósito: ver quantas pessoas
            já votaram antes de votar influencia o próprio voto.
          </p>
        </>
      ),
    },
    {
      titulo: 'O que fica de fora',
      conteudo: (
        <p>
          Atraso de mais de {legado} dias não entra nos cartões: é passivo antigo, não fila de
          cobrança. O número aparece embaixo dos cartões, como contexto, para você saber que ele
          existe sem que ele afogue os casos recuperáveis.
        </p>
      ),
    },
    {
      titulo: 'O papel da inteligência artificial',
      conteudo: (
        <p>
          O parágrafo acima dos cartões é escrito por inteligência artificial, mas{' '}
          <strong>ela não calcula nada</strong>: recebe as quantidades prontas e ajuda a priorizar.
          Quando não está disponível, o texto vem do próprio sistema e os números continuam os
          mesmos.
        </p>
      ),
    },
  ];
}

const ROTULOS: Record<string, string> = {
  'vencidos-recentes': 'deixaram de pagar nos últimos 30 dias',
  'atraso-em-recuperacao': 'atraso entre 31 e 90 dias',
  reincidentes: 'com dois ou mais boletos em aberto',
  regularizados: 'regularizaram nos últimos 30 dias',
};

function rotuloDoCard(id: string): string {
  return ROTULOS[id] ?? id;
}

export default RadarRelacionamentoPanel;
